<template>
  <div class="just-made-payment" v-if="payment">
    <div class="pb-3 alert alert-success">{{ $t('payment_accepted') }}</div>
    <div class="row no-gutters gap-2">
      <div class="col-12">
        <div class="form-label">{{ $t('print') }}</div>
      </div>
      <div class="col-auto">
        <button @click="printReceipt"
           class="btn-themed btn-themed-squared btn-sm btn-themed-squared-rounder">
          {{ $t('receipt') }}
        </button>
      </div>
      <div class="col-auto">
        <button @click="printCheque"
           class="btn-themed btn-themed-squared btn-sm btn-themed-squared-rounder">
          {{ $t('cheque') }}
        </button>
      </div>
    </div>
    <div class="row no-gutters mt-3 gap-2">
      <div class="col-12">
        <div class="form-label">{{ $t('send') }}</div>
      </div>
      <div class="col-auto">
        <SendThroughMessengerButton @sendClicked="sendReceipt">
          {{ $t('send_receipt_to_patient') }}
        </SendThroughMessengerButton>
      </div>
      <div class="col-auto"
           v-if="ppo && payment.uuid && !kkmEnabled">
        <button @click="printPPOCheque"
           class="btn-themed btn-themed-squared btn-sm btn-themed-squared-rounder">
          {{ $t('fiscal_cheque') }}
        </button>
      </div>
      <div class="col-auto" v-if="canSendLinkToFeedback">
        <SendThroughMessengerButton @sendClicked="sendLinkToFeedback">
          {{ $t('send_feedback_link_to_patient') }}
        </SendThroughMessengerButton>
      </div>
    </div>
    <div class="row no-gutters gap-2 mt-3"
         v-if="amountNumeric < leftToPay">
      <div class="col-12">
        <div class="form-label">{{ $t('continue') }}</div>
      </div>
      <div class="col-auto">
        <button @click="acceptAnotherPayment"
           class="btn-themed btn-themed-squared btn-sm btn-themed-squared-rounder">
          + {{ $t('accept_another_payment') }}
        </button>
      </div>
    </div>
  </div>
</template>

<script setup>
import JwtService from "@/services/jwt.service"
import {computed, getCurrentInstance, onBeforeMount} from "vue"
import {useStore} from "vuex"
import {sendReceiptToMessenger} from "@/services/payment.helper"
import SendThroughMessengerButton from "@/components/parts/crm/SendThroughMessengerButton.vue"
import {OnlineBookingService} from "@/services/api/online-booking.api.service"

const props = defineProps({
  payment: Object,
  payments: Array,
  ppo: Boolean,
  amountNumeric: Number,
  leftToPay: Number,
})

const emit = defineEmits(['acceptAnotherPayment'])

const store = useStore()
const getters = store.getters

let i18n = null
let noty = null

const clinic = computed(() => store.state.auth.clinic)
const kkmEnabled = computed(() => getters.kkmEnabled)
const rights = computed(() => store.state.auth.rights)
const hasOnlineBooking = computed(() => rights.value.includes('online_booking'))
const canSendLinkToFeedback = computed(() => {
  return hasOnlineBooking.value && props.payment?.treatment_id && props.payment?.client_id
})

onBeforeMount(() => {
  i18n = (getCurrentInstance())?.ctx?.$i18n
  noty = getCurrentInstance()?.appContext?.app?.noty
})

const printReceipt = () => {
  if(!props.payment) return
  let link = JwtService.getPrintPrelink(clinic.value)
  const path = link+'/receipt/'+props.payment.id+additionalPaymentsParam()
  window.open(path, '_blank')
}

const sendReceipt = async (messenger = 'whatsapp') => {
  if(!props.payment) return
  if(!confirm(i18n.t('do_you_confirm_sending_receipt_to_patient_messenger'))) return
  await sendReceiptToMessenger(clinic.value, props.payment.id, noty, i18n.t, messenger)
}

const sendLinkToFeedback = async (messenger = 'whatsapp') => {
  if(!canSendLinkToFeedback.value) return
  if(!confirm(i18n.t('do_you_confirm_sending_feedback_url_to_patient_messenger'))) return
  try {
    const res = await OnlineBookingService.sendFeedbackUrlToPatientThroughMessenger({
      messenger,
      record_id: props.payment?.treatment_id,
      patient_id: props.payment?.client_id
    })
    if(res.data) {
      noty.info(i18n.t('sent'))
    } else {
      noty.error(i18n.t('sent_error'))
    }
  } catch (e) {
    noty.error(i18n.t('sent_error'))
  }
}

const printCheque = () => {
  if(!props.payment) return
  let link = JwtService.getPrintPrelink(clinic.value)
  const path = link+'/cheque/'+props.payment.id+additionalPaymentsParam()
  window.open(path, '_blank')
}

const additionalPaymentsParam = () => {
  if((props.payments || []).length > 1) {
    return `?payments=${props.payments.map(p => p.id).join(',')}`
  }
  return ''
}

const printPPOCheque = () => {
  const uuid = props.payment.local_uuid || props.payment.uuid
  if(!uuid) return
  let link = JwtService.getPrintPrelink(clinic.value)
  window.open(link+'/cashdesk/cheque/'+uuid, '_blank')
}

const acceptAnotherPayment = async () => {
  emit('acceptAnotherPayment')
}
</script>

<style lang="scss" scoped>
@media screen and (max-width: 768px) {
  .just-made-payment {
    .btn-themed {
      width: 100%;
      margin-bottom: 15px;
      text-align: center;
    }
  }
}
</style>
